import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import UserService from '@/services/userService'
import { useErrorHandler } from '@/composables/useErrorHandler'
import type { 
  UserProfile,
  WalletInfo, 
  OrderRecord, 
  TransactionRecord, 
  ChatMessage, 
  ChatSession,
  BankAccountInfo,
  RechargeGuide
} from '@/types/user'
import type {
  ExtendedUserProfile,
  SystemNotification,
  ProjectStatistics,
  PaymentAddress,
  TransactionSummary,
  MembershipInfoResponse
} from '@/types/personal-center'

export const useUserStore = defineStore('user', () => {
  // 错误处理
  const { withErrorHandling } = useErrorHandler({
    context: 'UserStore',
    autoRetry: true,
    maxRetries: 3
  })

  // 状态
  const profile = ref<UserProfile | null>(null)
  const extendedProfile = ref<ExtendedUserProfile | null>(null)
  const walletInfo = ref<WalletInfo | null>(null)
  const orderRecords = ref<OrderRecord[]>([])
  const transactionHistory = ref<TransactionRecord[]>([])
  const chatSession = ref<ChatSession | null>(null)
  const chatMessages = ref<ChatMessage[]>([])
  const bankAccount = ref<BankAccountInfo | null>(null)
  const rechargeGuide = ref<RechargeGuide | null>(null)
  
  // 个人中心重新设计 - 新增状态
  const membershipInfo = ref<MembershipInfoResponse | null>(null)
  const systemNotifications = ref<SystemNotification[]>([])
  const projectStatistics = ref<ProjectStatistics | null>(null)
  const paymentAddresses = ref<PaymentAddress[]>([])
  const transactionSummary = ref<TransactionSummary | null>(null)
  
  // 资产相关状态
  const assetSummary = ref<any>(null)
  const assetBreakdown = ref<any[]>([])
  const assetHistory = ref<any[]>([])
  
  // 有效期
  const validityPeriod = computed(() => {
    if (!profile.value) return 'Loading...';
    const date = new Date(profile.value.validity_period);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  });
  
  // 加载状态
  const loading = ref({
    profile: false,
    extendedProfile: false,
    wallet: false,
    orders: false,
    transactions: false,
    chat: false,
    bankAccount: false,
    rechargeGuide: false,
    // 个人中心重新设计 - 新增加载状态
    membership: false,
    notifications: false,
    projectStats: false,
    paymentAddresses: false,
    transactionSummary: false,
    // 资产相关加载状态
    assetSummary: false,
    assetBreakdown: false,
    assetHistory: false
  })

  // 分页信息
  const pagination = ref({
    orders: {
      current_page: 1,
      last_page: 1,
      total: 0
    },
    transactions: {
      current_page: 1,
      last_page: 1,
      total: 0
    },
    messages: {
      current_page: 1,
      last_page: 1,
      total: 0
    },
    // 个人中心重新设计 - 新增分页信息
    notifications: {
      current_page: 1,
      last_page: 1,
      total: 0,
      unread_count: 0
    }
  })

  // 计算属性
  const fullName = computed(() => 
    profile.value ? `${profile.value.first_name} ${profile.value.last_name}` : 'Loading...'
  )

  const totalPurchaseAmount = computed(() => 
    orderRecords.value
      .filter(order => order.status === 'completed')
      .reduce((sum, order) => sum + order.total_price, 0)
  )

  const totalSalesAmount = computed(() => 
    transactionHistory.value
      .filter(tx => tx.type === 'sale')
      .reduce((sum, tx) => sum + tx.amount, 0)
  )

  const unreadMessageCount = computed(() => 
    chatSession.value?.unread_count || 0
  )

  // 个人中心重新设计 - 新增计算属性
  const unreadNotificationCount = computed(() => 
    pagination.value.notifications.unread_count
  )

  const currentMemberLevel = computed(() => 
    membershipInfo.value?.current_level.level || 'P1'
  )

  const memberTitle = computed(() => 
    membershipInfo.value?.current_level.title || 'Member'
  )

  const isAccountFrozen = computed(() => 
    extendedProfile.value?.account_frozen || false
  )

  const hasWithdrawPin = computed(() => 
    extendedProfile.value?.withdraw_pin_set || false
  )

  const totalCarbonCredits = computed(() => 
    walletInfo.value?.carbon_credits || 0
  )

  // 获取用户个人资料
  const fetchProfile = async () => {
    const result = await withErrorHandling(
      async () => {
        loading.value.profile = true
        profile.value = await UserService.getProfile()
        return profile.value
      },
      '获取用户资料...',
      () => UserService.getProfile()
    )
    
    loading.value.profile = false
    return result
  }

  // 更新用户头像
  const updateAvatar = async (file: File) => {
    try {
      const avatarUrl = await UserService.updateAvatar(file)
      if (profile.value) {
        profile.value.avatar_url = avatarUrl
      }
      return avatarUrl
    } catch (error) {
      console.error('更新头像失败:', error)
      throw error
    }
  }

  // 获取钱包信息
  const fetchWalletInfo = async () => {
    const result = await withErrorHandling(
      async () => {
        loading.value.wallet = true
        walletInfo.value = await UserService.getWalletInfo()
        return walletInfo.value
      },
      '获取钱包信息...',
      () => UserService.getWalletInfo()
    )
    
    loading.value.wallet = false
    return result
  }

  // 获取订单记录
  const fetchOrderRecords = async (page = 1, append = false) => {
    loading.value.orders = true
    try {
      const result = await UserService.getOrderRecords(page)
      
      if (append) {
        orderRecords.value.push(...result.orders)
      } else {
        orderRecords.value = result.orders
      }
      
      pagination.value.orders = {
        current_page: result.current_page,
        last_page: result.last_page,
        total: result.total
      }
    } catch (error) {
      console.error('获取订单记录失败:', error)
      throw error
    } finally {
      loading.value.orders = false
    }
  }

  // 获取交易历史
  const fetchTransactionHistory = async (page = 1, append = false) => {
    loading.value.transactions = true
    try {
      const result = await UserService.getTransactionHistory(page)
      
      if (append) {
        transactionHistory.value.push(...result.transactions)
      } else {
        transactionHistory.value = result.transactions
      }
      
      pagination.value.transactions = {
        current_page: result.current_page,
        last_page: result.last_page,
        total: result.total
      }
    } catch (error) {
      console.error('获取交易历史失败:', error)
      throw error
    } finally {
      loading.value.transactions = false
    }
  }

  // 修改密码
  const changePassword = async (data: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }) => {
    try {
      await UserService.changePassword(data)
    } catch (error) {
      console.error('修改密码失败:', error)
      throw error
    }
  }

  // 获取银行账户信息
  const fetchBankAccount = async () => {
    loading.value.bankAccount = true
    try {
      bankAccount.value = await UserService.getBankAccount()
    } catch (error) {
      console.error('获取银行账户信息失败:', error)
      throw error
    } finally {
      loading.value.bankAccount = false
    }
  }

  // 更新银行账户信息
  const updateBankAccount = async (data: BankAccountInfo) => {
    try {
      await UserService.updateBankAccount(data)
      bankAccount.value = { ...bankAccount.value, ...data }
    } catch (error) {
      console.error('更新银行账户信息失败:', error)
      throw error
    }
  }

  // 获取聊天会话
  const fetchChatSession = async () => {
    loading.value.chat = true
    try {
      chatSession.value = await UserService.getChatSession()
    } catch (error) {
      console.error('获取聊天会话失败:', error)
      throw error
    } finally {
      loading.value.chat = false
    }
  }

  // 获取聊天消息
  const fetchChatMessages = async (sessionId: number, page = 1, append = false) => {
    try {
      const result = await UserService.getChatMessages(sessionId, page)
      
      if (append) {
        chatMessages.value.unshift(...result.messages.reverse())
      } else {
        chatMessages.value = result.messages
      }
      
      pagination.value.messages = {
        current_page: result.current_page,
        last_page: result.last_page,
        total: result.total
      }
    } catch (error) {
      console.error('获取聊天消息失败:', error)
      throw error
    }
  }

  // 发送文字消息
  const sendTextMessage = async (sessionId: number, message: string) => {
    try {
      const newMessage = await UserService.sendTextMessage(sessionId, message)
      chatMessages.value.push(newMessage)
      return newMessage
    } catch (error) {
      console.error('发送消息失败:', error)
      throw error
    }
  }

  // 发送图片消息
  const sendImageMessage = async (sessionId: number, file: File) => {
    try {
      const newMessage = await UserService.sendImageMessage(sessionId, file)
      chatMessages.value.push(newMessage)
      return newMessage
    } catch (error) {
      console.error('发送图片失败:', error)
      throw error
    }
  }

  // 标记消息为已读
  const markMessagesAsRead = async (sessionId: number) => {
    try {
      await UserService.markMessagesAsRead(sessionId)
      if (chatSession.value) {
        chatSession.value.unread_count = 0
      }
    } catch (error) {
      console.error('标记消息已读失败:', error)
      throw error
    }
  }

  // 获取充值指南
  const fetchRechargeGuide = async () => {
    loading.value.rechargeGuide = true
    try {
      rechargeGuide.value = await UserService.getRechargeGuide()
    } catch (error) {
      console.error('获取充值指南失败:', error)
      throw error
    } finally {
      loading.value.rechargeGuide = false
    }
  }

  // 创建充值申请
  const createRechargeRequest = async (data: {
    amount: number
    payment_method_id: number
    payment_proof?: File
    note?: string
  }) => {
    try {
      const result = await UserService.createRechargeRequest(data)
      return result
    } catch (error) {
      console.error('创建充值申请失败:', error)
      throw error
    }
  }

  // ===== 个人中心重新设计 - 新增方法 =====

  // 获取扩展用户资料
  const fetchExtendedProfile = async () => {
    loading.value.extendedProfile = true
    try {
      extendedProfile.value = await UserService.getExtendedProfile()
    } catch (error) {
      console.error('获取扩展用户资料失败:', error)
      throw error
    } finally {
      loading.value.extendedProfile = false
    }
  }

  // 获取会员等级信息
  const fetchMembershipInfo = async () => {
    loading.value.membership = true
    try {
      const result = await UserService.getMembershipInfo()
      membershipInfo.value = {
        current_level: result.current_level,
        next_level: result.next_level,
        progress_to_next: result.progress_to_next,
        benefits_unlocked: [],
        upgrade_requirements: result.next_level ? {
          investment_needed: result.next_level.requirements.min_investment,
          carbon_credits_needed: result.next_level.requirements.min_carbon_credits
        } : undefined
      }
    } catch (error) {
      console.error('获取会员等级信息失败:', error)
      throw error
    } finally {
      loading.value.membership = false
    }
  }

  // 获取系统通知
  const fetchSystemNotifications = async (page = 1, append = false) => {
    loading.value.notifications = true
    try {
      const result = await UserService.getSystemNotifications(page)
      
      if (append) {
        systemNotifications.value.push(...result.notifications)
      } else {
        systemNotifications.value = result.notifications
      }
      
      pagination.value.notifications = {
        current_page: result.current_page,
        last_page: result.last_page,
        total: result.total,
        unread_count: result.unread_count
      }
    } catch (error) {
      console.error('获取系统通知失败:', error)
      throw error
    } finally {
      loading.value.notifications = false
    }
  }

  // 标记通知为已读
  const markNotificationAsRead = async (notificationId: number) => {
    try {
      await UserService.markNotificationAsRead(notificationId)
      
      // 更新本地状态
      const notification = systemNotifications.value.find(n => n.id === notificationId)
      if (notification) {
        notification.is_read = true
        pagination.value.notifications.unread_count = Math.max(0, pagination.value.notifications.unread_count - 1)
      }
    } catch (error) {
      console.error('标记通知已读失败:', error)
      throw error
    }
  }

  // 标记所有通知为已读
  const markAllNotificationsAsRead = async () => {
    try {
      await UserService.markAllNotificationsAsRead()
      
      // 更新本地状态
      systemNotifications.value.forEach(notification => {
        notification.is_read = true
      })
      pagination.value.notifications.unread_count = 0
    } catch (error) {
      console.error('标记所有通知已读失败:', error)
      throw error
    }
  }

  // 更新未读通知数量
  const updateUnreadNotificationCount = (count: number) => {
    pagination.value.notifications.unread_count = count
  }

  // 获取项目统计
  const fetchProjectStatistics = async () => {
    loading.value.projectStats = true
    try {
      projectStatistics.value = await UserService.getProjectStatistics()
    } catch (error) {
      console.error('获取项目统计失败:', error)
      throw error
    } finally {
      loading.value.projectStats = false
    }
  }

  // 获取收款地址列表
  const fetchPaymentAddresses = async () => {
    loading.value.paymentAddresses = true
    try {
      paymentAddresses.value = await UserService.getPaymentAddresses()
    } catch (error) {
      console.error('获取收款地址列表失败:', error)
      throw error
    } finally {
      loading.value.paymentAddresses = false
    }
  }

  // 添加收款地址（支持多鏈類型）
  const addPaymentAddress = async (data: {
    type: 'bank' | 'btc' | 'eth' | 'sol' | 'bnb' | 'trc'
    label: string
    address: string
  }) => {
    try {
      const newAddress = await UserService.addPaymentAddress(data)
      paymentAddresses.value.push(newAddress)
      return newAddress
    } catch (error) {
      console.error('添加收款地址失败:', error)
      throw error
    }
  }

  // 更新收款地址（支持修改鏈類型）
  const updatePaymentAddress = async (addressId: number, data: {
    type?: 'bank' | 'btc' | 'eth' | 'sol' | 'bnb' | 'trc'
    label: string
    address: string
  }) => {
    try {
      const updatedAddress = await UserService.updatePaymentAddress(addressId, data)
      const index = paymentAddresses.value.findIndex(addr => addr.id === addressId)
      if (index !== -1) {
        paymentAddresses.value[index] = updatedAddress
      }
      return updatedAddress
    } catch (error) {
      console.error('更新收款地址失败:', error)
      throw error
    }
  }

  // 删除收款地址
  const deletePaymentAddress = async (addressId: number) => {
    try {
      await UserService.deletePaymentAddress(addressId)
      const index = paymentAddresses.value.findIndex(addr => addr.id === addressId)
      if (index !== -1) {
        paymentAddresses.value.splice(index, 1)
      }
    } catch (error) {
      console.error('删除收款地址失败:', error)
      throw error
    }
  }

  // 设置提现密码
  const setWithdrawPin = async (pin: string) => {
    try {
      await UserService.setWithdrawPin(pin)
      if (extendedProfile.value) {
        extendedProfile.value.withdraw_pin_set = true
        extendedProfile.value.failed_withdraw_attempts = 0
      }
    } catch (error) {
      console.error('设置提现密码失败:', error)
      throw error
    }
  }

  // 验证提现密码
  const verifyWithdrawPin = async (pin: string) => {
    try {
      const result = await UserService.verifyWithdrawPin(pin)
      
      // 更新失败次数
      if (!result.valid && extendedProfile.value && result.remaining_attempts !== undefined) {
        extendedProfile.value.failed_withdraw_attempts = 3 - result.remaining_attempts
        
        // 如果达到最大失败次数，冻结账号
        if (result.remaining_attempts === 0) {
          extendedProfile.value.account_frozen = true
        }
      }
      
      return result
    } catch (error) {
      console.error('验证提现密码失败:', error)
      throw error
    }
  }

    // 验证提现密码
  const verifyPassword = async (pin: string) => {
    try {
      const result = await UserService.verifyPassword(pin)
      
      // 更新失败次数
      if (!result.valid && extendedProfile.value && result.remaining_attempts !== undefined) {
        extendedProfile.value.failed_withdraw_attempts = 3 - result.remaining_attempts
        
        // 如果达到最大失败次数，冻结账号
        if (result.remaining_attempts === 0) {
          extendedProfile.value.account_frozen = true
        }
      }
      
      return result
    } catch (error) {
      console.error('验证登录密码失败:', error)
      throw error
    }
  }

  // 重置提现密码错误次数
  const resetWithdrawAttempts = async () => {
    try {
      await UserService.resetWithdrawAttempts()
      if (extendedProfile.value) {
        extendedProfile.value.failed_withdraw_attempts = 0
        extendedProfile.value.account_frozen = false
      }
    } catch (error) {
      console.error('重置提现密码错误次数失败:', error)
      throw error
    }
  }

  // 创建提现申请
  const createWithdrawRequest = async (data: {
    amount: number
    address_id: number
    withdraw_pin: string
  }) => {
    try {
      const result = await UserService.createWithdrawRequest(data)
      
      // 刷新钱包信息
      await fetchWalletInfo()
      
      return result
    } catch (error) {
      console.error('创建提现申请失败:', error)
      throw error
    }
  }

  // 获取转账历史摘要
  const fetchTransactionSummary = async () => {
    loading.value.transactionSummary = true
    try {
      transactionSummary.value = await UserService.getTransactionSummary()
    } catch (error) {
      console.error('获取转账历史摘要失败:', error)
      throw error
    } finally {
      loading.value.transactionSummary = false
    }
  }

  // 更新用户个人信息
  const updateProfile = async (data: {
    license_passport?: string
    first_name?: string
    last_name?: string
  }) => {
    try {
      const updatedProfile = await UserService.updateProfile(data)
      extendedProfile.value = updatedProfile
      // 同时更新基础profile
      if (profile.value) {
        if (data.first_name) profile.value.first_name = data.first_name
        if (data.last_name) profile.value.last_name = data.last_name
      }
      
      return updatedProfile
    } catch (error) {
      console.error('更新用户个人信息失败:', error)
      throw error
    }
  }

  // ===== 资产管理相关方法 =====

  // 获取资产摘要
  const fetchAssetSummary = async () => {
    loading.value.assetSummary = true
    try {
      assetSummary.value = await UserService.getAssetSummary()
      return assetSummary.value
    } catch (error) {
      console.error('获取资产摘要失败:', error)
      throw error
    } finally {
      loading.value.assetSummary = false
    }
  }

  // 获取资产明细分解
  const fetchAssetBreakdown = async () => {
    loading.value.assetBreakdown = true
    try {
      assetBreakdown.value = await UserService.getAssetBreakdown()
      return assetBreakdown.value
    } catch (error) {
      console.error('获取资产明细失败:', error)
      throw error
    } finally {
      loading.value.assetBreakdown = false
    }
  }

  // 获取资产变化历史
  const fetchAssetHistory = async (timeRange = '30d') => {
    loading.value.assetHistory = true
    try {
      assetHistory.value = await UserService.getAssetHistory(timeRange)
      return assetHistory.value
    } catch (error) {
      console.error('获取资产历史失败:', error)
      throw error
    } finally {
      loading.value.assetHistory = false
    }
  }

  // 计算售卖影响
  const calculateSaleImpact = async (saleQuantity: number, buyerPrice: number) => {
    try {
      return await UserService.calculateSaleImpact(saleQuantity, buyerPrice)
    } catch (error) {
      console.error('计算售卖影响失败:', error)
      throw error
    }
  }



  // 重置状态
  const resetState = () => {
    profile.value = null
    extendedProfile.value = null
    walletInfo.value = null
    orderRecords.value = []
    transactionHistory.value = []
    chatSession.value = null
    chatMessages.value = []
    bankAccount.value = null
    rechargeGuide.value = null
    
    // 个人中心重新设计 - 重置新增状态
    membershipInfo.value = null
    systemNotifications.value = []
    projectStatistics.value = null
    paymentAddresses.value = []
    transactionSummary.value = null
    
    // 重置资产相关状态
    assetSummary.value = null
    assetBreakdown.value = []
    assetHistory.value = []
    
    // 重置加载状态
    Object.keys(loading.value).forEach(key => {
      loading.value[key as keyof typeof loading.value] = false
    })
    
    // 重置分页信息
    pagination.value = {
      orders: { current_page: 1, last_page: 1, total: 0 },
      transactions: { current_page: 1, last_page: 1, total: 0 },
      messages: { current_page: 1, last_page: 1, total: 0 },
      notifications: { current_page: 1, last_page: 1, total: 0, unread_count: 0 }
    }
  }

  return {
    // 状态
    profile,
    extendedProfile,
    walletInfo,
    orderRecords,
    transactionHistory,
    chatSession,
    chatMessages,
    bankAccount,
    rechargeGuide,
    membershipInfo,
    systemNotifications,
    projectStatistics,
    paymentAddresses,
    transactionSummary,
    assetSummary,
    assetBreakdown,
    assetHistory,
    loading,
    pagination,
    validityPeriod,
    
    // 计算属性
    fullName,
    totalPurchaseAmount,
    totalSalesAmount,
    unreadMessageCount,
    unreadNotificationCount,
    currentMemberLevel,
    memberTitle,
    isAccountFrozen,
    hasWithdrawPin,
    totalCarbonCredits,
    verifyPassword,
    
    // 原有方法
    fetchProfile,
    updateAvatar,
    fetchWalletInfo,
    fetchOrderRecords,
    fetchTransactionHistory,
    changePassword,
    fetchBankAccount,
    updateBankAccount,
    fetchChatSession,
    fetchChatMessages,
    sendTextMessage,
    sendImageMessage,
    markMessagesAsRead,
    fetchRechargeGuide,
    createRechargeRequest,
    resetState,
    
    // 个人中心重新设计 - 新增方法
    fetchExtendedProfile,
    fetchMembershipInfo,
    fetchSystemNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    updateUnreadNotificationCount,
    fetchProjectStatistics,
    fetchPaymentAddresses,
    addPaymentAddress,
    updatePaymentAddress,
    deletePaymentAddress,
    setWithdrawPin,
    verifyWithdrawPin,
    resetWithdrawAttempts,
    createWithdrawRequest,
    fetchTransactionSummary,
    updateProfile,
    
    // 资产管理相关方法
    fetchAssetSummary,
    fetchAssetBreakdown,
    fetchAssetHistory,
    calculateSaleImpact
  }
})